import { useQuery } from "@tanstack/react-query"

import { getKPIRequest } from "@/api/getKPIRequest.ts"
import MultiGauge from "@/components/MultiGauge.tsx"

import GaugeCard from "./GaugeCard.tsx"

import type { FlowRateDisplayData } from "../types/flowRateDisplayTypes.ts"

export default function FlowRateGauge({
  fetcher,
  location,
}: {
  fetcher: () => Promise<FlowRateDisplayData>
  location: number
}) {
  const { data, isPending, isError } = useQuery({
    queryKey: ["flowRateDisplay", { location }],
    queryFn: fetcher,
    refetchInterval: 1000,
  })

  const budgetQuery = useQuery({
    queryKey: ["gasToPipelineBudget", { location }],
    queryFn: () =>
      getKPIRequest({
        returnSize: 1,
        asOf: new Date(),
        kpis: [
          {
            $type: "basic",
            typeId: 2,
            location,
            aggFunction: "Average",
            id: 80,
          },
        ],
      }),
  })

  let thresholds = [500, 1000, 1250, 1500]

  if (budgetQuery.data && budgetQuery.data.length > 0) {
    const budget =
      budgetQuery.data[0].values && budgetQuery.data[0].values.length > 0
        ? budgetQuery.data[0].values[0].value
        : 0

    const lowerBound = budget * 0.8
    const upperBound = budget * 1.2
    const max = upperBound * 1.1
    thresholds = [lowerBound, budget, upperBound, max]
  }

  const statusText = isError
    ? "Error"
    : isPending || !data
      ? "N/A"
      : data.online
        ? "Running"
        : "Off"

  const statusClass =
    isError || (data && !data.online)
      ? "text-status-bad"
      : data?.online
        ? "text-metric-accent"
        : "text-muted-foreground"

  return (
    <GaugeCard
      title="Gas to Pipeline Flow Rate"
      subtitle="(MMBtu / Day)"
      status={
        <span
          className={`font-semibold text-2xl leading-7 pr-1 font-sans text-right shrink-0 min-w-[6rem] ${statusClass}`}
        >
          {statusText}
        </span>
      }
    >
      <MultiGauge
        thresholds={thresholds}
        colors={[
          "var(--status-bad)",
          "var(--status-warn)",
          "var(--status-good)",
          "var(--metric-accent)",
        ]}
        value={
          isPending || isError || !data
            ? null
            : ((data.outletSCFM * data.outletCH4) / 100) * 1.44 * 1.01
        }
        placeholder={isError ? "Error" : "—"}
        labels={true}
      />
    </GaugeCard>
  )
}
